(() => {
  const runtimeKey = 'PawlishBeforeAfterPremiumRuntime';
  const runtimeVersion = 3;
  const existingRuntime = window[runtimeKey];

  if (
    existingRuntime &&
    existingRuntime.version === runtimeVersion &&
    typeof existingRuntime.init === 'function'
  ) {
    existingRuntime.init(document);
    return;
  }

  if (existingRuntime && typeof existingRuntime.destroyRuntime === 'function') {
    existingRuntime.destroyRuntime();
  }

  const globalController = new AbortController();
  const instances = new WeakMap();

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const collectRoots = (scope) => {
    const roots = [];
    if (!scope) return roots;

    if (scope instanceof Element && scope.matches('[data-pbap-root]')) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      scope.querySelectorAll('[data-pbap-root]').forEach((root) => roots.push(root));
    }

    return roots;
  };

  const createInstance = (root) => {
    const range = root.querySelector('[data-pbap-range]');
    const compare = root.querySelector('[data-pbap-compare]');
    if (!(range instanceof HTMLInputElement) || !(compare instanceof Element)) return null;

    const controller = new AbortController();
    const beforeLabel = compare.querySelector('.pro-before-after-premium__label--before');
    const afterLabel = compare.querySelector('.pro-before-after-premium__label--after');

    let isDragging = false;
    let activePointerId = null;
    let beforeHideAt = null;
    let afterHideAt = null;

    const readRangeValue = () => clamp(Number.parseFloat(range.value) || 0, 0, 100);

    const measureLabelEdges = () => {
      const compareRect = compare.getBoundingClientRect();

      if (compareRect.width <= 0) {
        beforeHideAt = null;
        afterHideAt = null;
        return;
      }

      if (beforeLabel) {
        const beforeRect = beforeLabel.getBoundingClientRect();
        beforeHideAt = clamp(((beforeRect.right - compareRect.left) / compareRect.width) * 100, 0, 100);
      } else {
        beforeHideAt = null;
      }

      if (afterLabel) {
        const afterRect = afterLabel.getBoundingClientRect();
        afterHideAt = clamp(((afterRect.left - compareRect.left) / compareRect.width) * 100, 0, 100);
      } else {
        afterHideAt = null;
      }
    };

    const update = () => {
      const value = readRangeValue();

      compare.style.setProperty('--pbap-pos', `${value}%`);
      compare.classList.toggle(
        'is-before-label-hidden',
        beforeHideAt !== null && value <= beforeHideAt
      );
      compare.classList.toggle(
        'is-after-label-hidden',
        afterHideAt !== null && value >= afterHideAt
      );
      range.setAttribute('aria-valuetext', `${Math.round(value)}% before`);
    };

    const setFromClientX = (clientX) => {
      const rect = compare.getBoundingClientRect();
      if (!rect.width) return;

      const value = clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
      range.value = String(Math.round(value * 10) / 10);
      update();
    };

    const endPointerDrag = (event) => {
      if (!isDragging) return;
      if (activePointerId !== null && event.pointerId !== activePointerId) return;

      isDragging = false;
      compare.classList.remove('is-dragging');

      try {
        if (activePointerId !== null) compare.releasePointerCapture(activePointerId);
      } catch (error) {
        // Pointer capture may already have been released by the browser.
      }

      activePointerId = null;
    };

    compare.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        isDragging = true;
        activePointerId = event.pointerId;
        compare.classList.add('is-dragging');

        measureLabelEdges();

        try {
          compare.setPointerCapture(activePointerId);
        } catch (error) {
          // Continue without pointer capture when the browser refuses it.
        }

        setFromClientX(event.clientX);
        event.preventDefault();
      },
      { signal: controller.signal }
    );

    compare.addEventListener(
      'pointermove',
      (event) => {
        if (!isDragging || event.pointerId !== activePointerId) return;

        setFromClientX(event.clientX);
        event.preventDefault();
      },
      { signal: controller.signal }
    );

    compare.addEventListener('pointerup', endPointerDrag, { signal: controller.signal });
    compare.addEventListener('pointercancel', endPointerDrag, { signal: controller.signal });
    compare.addEventListener(
      'lostpointercapture',
      () => {
        isDragging = false;
        activePointerId = null;
        compare.classList.remove('is-dragging');
      },
      { signal: controller.signal }
    );

    range.addEventListener('input', update, { signal: controller.signal });
    range.addEventListener('change', update, { signal: controller.signal });

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => {
          measureLabelEdges();
          update();
        })
      : null;

    resizeObserver?.observe(compare);
    if (beforeLabel) resizeObserver?.observe(beforeLabel);
    if (afterLabel) resizeObserver?.observe(afterLabel);

    measureLabelEdges();
    update();

    return {
      destroy() {
        resizeObserver?.disconnect();
        controller.abort();
        compare.classList.remove(
          'is-dragging',
          'is-before-label-hidden',
          'is-after-label-hidden'
        );
      }
    };
  };

  const init = (scope = document) => {
    collectRoots(scope).forEach((root) => {
      if (instances.has(root)) return;
      const instance = createInstance(root);
      if (instance) instances.set(root, instance);
    });
  };

  const destroy = (scope) => {
    collectRoots(scope).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  document.addEventListener(
    'shopify:section:load',
    (event) => init(event.target),
    { signal: globalController.signal }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => destroy(event.target),
    { signal: globalController.signal }
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), {
      once: true,
      signal: globalController.signal
    });
  } else {
    init(document);
  }

  window[runtimeKey] = {
    version: runtimeVersion,
    init,
    destroy,
    destroyRuntime() {
      globalController.abort();
      destroy(document);
      if (window[runtimeKey] === this) delete window[runtimeKey];
    }
  };
})();
